import { Injectable } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { Observable } from 'rxjs';

/**
 * Interface for components that can have unsaved changes.
 * All item components (TestCase, TestSuite, etc.) implement this via their hasChangeSignal() method.
 */
export interface CanComponentDeactivate {
  hasChange?: boolean; // Legacy - will be removed after full migration
  hasChangeSignal?: () => boolean; // Signal-based change detection
}

/**
 * Guard that warns users when navigating away from a component with unsaved changes.
 * Applied to all item detail routes.
 *
 * Improvements implemented:
 * - Better dialog messaging with clear action options
 * - Route context awareness (current/next state)
 * - Only fires when hasChange is truly set by user edits (not system changes)
 */
@Injectable({
  providedIn: 'root'
})
export class UnsavedChangesGuard implements CanDeactivate<CanComponentDeactivate> {
  canDeactivate(
    component: CanComponentDeactivate
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Check for unsaved changes using either new signal-based or legacy property-based detection
    const hasChanges = component.hasChangeSignal?.() ?? component.hasChange ?? false;

    console.log('[UnsavedChangesGuard] canDeactivate', {
      hasChanges,
      shouldWarn: hasChanges
    });

    // Allow navigation if no changes
    if (!hasChanges) {
      return true;
    }

    // Show confirmation dialog if there are unsaved changes
    const message = 'You have unsaved changes that will be lost.\n\nClick OK to leave without saving, or Cancel to stay on this page.';

    const result = window.confirm(message);
    console.log('[UnsavedChangesGuard] User chose:', result ? 'Leave' : 'Stay');
    return result;
  }
}
